import { Label } from "@radix-ui/react-label";
import { useId, useMemo, type ComponentProps } from "react";
import {
    get,
    useFormContext,
    type FieldError,
    type FieldValues,
    type Path,
} from "react-hook-form";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import RequiredMark from "./RequiredMark";
import { useSchema } from "@/v2/hooks/SchemaContext";
import { ZodArray } from "zod";

type FormFieldBaseProps = Omit<ComponentProps<typeof Input>, "type">;

interface ArFormNumberFieldProps<
    T extends FieldValues,
> extends FormFieldBaseProps {
    name: Path<T>;
    label: string;
    labelAlign?: "items-end" | "items-start" | "items-center";
    min?: number;
    max?: number;
    step?: number;
}

export function ArNumberFormField<T extends FieldValues>({
    label,
    name,
    placeholder,
    labelAlign,
    min,
    max,
    step,
    ...rest
}: ArFormNumberFieldProps<T>) {
    const {
        register,
        formState: { errors },
    } = useFormContext<T>();

    const error = get(errors, name) as FieldError | undefined;
    const id = useId();
    const schema = useSchema();

    const isRequired = useMemo(() => {
        const parts = name.split(".");
        let currentSchema: any = schema;

        for (const part of parts) {
            if (!currentSchema?.shape) return false;

            // Handle array index (numeric parts)
            if (/^\d+$/.test(part)) {
                if (currentSchema instanceof ZodArray) {
                    currentSchema = currentSchema.element;
                }
                continue;
            }

            currentSchema = currentSchema.shape[part];

            if (currentSchema instanceof ZodArray) {
                currentSchema = currentSchema.element;
            }
        }

        const testResult = currentSchema?.safeParse("");
        return !testResult?.success;
    }, [schema, name]);

    return (
        <div className={cn("flex flex-col gap-1", labelAlign && labelAlign)}>
            <Label
                htmlFor={id}
                className={cn(
                    "text-sm font-medium",
                    error && "text-destructive",
                )}
            >
                {label}
                {isRequired && <RequiredMark />}
            </Label>
            <Input
                placeholder={placeholder}
                className={cn(
                    error &&
                        "border-destructive focus-visible:ring-destructive",
                )}
                {...register(name, {
                    setValueAs: (v) => {
                        if (v === "" || v === null || v === undefined)
                            return undefined;
                        const num = Number(v);
                        return isNaN(num) ? v : num;
                    },
                    validate: (value) => {
                        if (value === undefined || value === "") return true;
                        const num = Number(value);
                        if (isNaN(num)) return "Please enter a valid number";
                        if (min !== undefined && num < min)
                            return `Minimum value is ${min}`;
                        if (max !== undefined && num > max)
                            return `Maximum value is ${max}`;
                        return true;
                    },
                })}
                {...rest}
                inputMode="decimal"
            />
            {error && (
                <p id={`${id}-error`} className="text-xs text-destructive">
                    {error.message as string}
                </p>
            )}
        </div>
    );
}
